<template>
  <div ref="note" :style="previewNoteStyle" :class="highlightColor" class="preview-note rounded-lg mx-3 my-1 p-2 px-4 select-none flex">
    <div class="flex flex-col-reverse w-full top-0 left-0">
      <div class="flex flex-row justify-between my-1">
        <div class="flex justify-between w-full items-baseline text-sm">
          <div
            class="flex text-base px-1 rounded-lg"
            :style="`background-color: ${color.actions.selectColor(props.previewNote.username).background};`"
          >
            <p class="text-left" :style="`color: ${color.actions.selectColor(props.previewNote.username).text};`">
              {{ props.previewNote.username }}
            </p>
          </div>
        </div>
      </div>
      <div class="flex flex-col">
        <p
          ref="newText"
          :class="getStyles(props.previewNote.highlightColor)"
          class="w-full bg-transparent text-left font-thin text-sm mt-1 break-words"
          type="text"
        >
          {{ extractTags(props.previewNote.text) }}
        </p>
        <div class="flex mt-2 flex-wrap mb-2">
          <div
            v-for="(tag, index) in condensedTags"
            :key="index"
            class="text-sm soft-btn-nh rounded-2xl px-2 py-1 mr-2 mt-2"
            :style="getKeywordStyle(props.previewNote.highlightColor)"
          >
            {{ tag }}
          </div>
          <div v-if="props.previewNote?.tags && props.previewNote?.tags?.length > 2" class="text-sm soft-btn-nh rounded-2xl px-2 py-1 mr-2 mt-2">
            ...
          </div>
          <div
            v-for="(url, index) in state.noteUrls"
            :key="index"
            class="text-sm text-white bg-slate-middle rounded-2xl px-2 py-1 mr-2 mt-2 cursor-pointer flex items-center"
          >
            <img class="invert w-4 mr-1.5" :src="Link" alt="url link" />
            <p>{{ common.actions.cleanUrlString(url) }}</p>
          </div>
        </div>
      </div>
      <div v-if="props.previewNote.attachment">
        <div v-if="props.previewNote.attachment.type === AttachmentType.image" class="rounded-t-lg pb-3 w-full max-h-52 self-center">
          <img
            class="pointer-events-none rounded-t-lg max-h-52 w-full object-cover"
            :style="imageStyle"
            :src="previewImage"
            :alt="t('aria-image-preview')"
          />
        </div>
        <div
          v-else-if="props.previewNote?.attachment?.type === AttachmentType.external"
          class="rounded-t-lg pb-3 w-full max-h-52 self-center"
          :style="imageStyle"
        >
          <img
            v-if="!state.imageErrorEncountered"
            class="pointer-events-none rounded-t-lg max-h-52 w-full object-cover"
            :src="previewImage"
            @error="imageLoadError($event)"
            @load="imageLoad($event)"
            alt="load error"
          />
          <div v-else class="rounded-t-lg p-4 text-white">
            <p class="font-bold">{{ t('nopreview') }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
  import { PropType, computed, onMounted, reactive, ref } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { PreviewNote } from '@/common/classes'
  import { ScreensizeType, AttachmentType, baseUrl } from '@/constants'

  import useColorStore from '@/composables/colors'
  import useCommonStore from '@/composables/common'

  import Link from '@/assets/icons/fontawesome/link-simple-regular.svg'
  import BrokenImage from '@/assets/icons/fontawesome/image-slash-solid.svg'

  const color = useColorStore()
  const common = useCommonStore()

  const note = ref<HTMLInputElement | null>(null)

  const props = defineProps({
    previewNote: { type: Object as PropType<PreviewNote>, required: true },
    length: { type: Number, required: true },
  })

  const state = reactive({
    noteUrls: [] as string[],
    actualUrls: [] as string[],
    imageErrorEncountered: false,
  })

  const { t } = useI18n({})

  onMounted(() => {
    state.noteUrls = common.actions.getActualUrlsFromText(props.previewNote.text)
    if (state.noteUrls.length > 2) state.noteUrls.splice(0, 2)
    state.actualUrls = common.actions.getActualUrlsFromText(props.previewNote.text)
  })

  const highlightColor = computed(() => {
    return props.previewNote.highlightColor !== '' ? props.previewNote.highlightColor : 'bg-slate-bright'
  })

  const imageStyle = computed(() => {
    return note.value ? `width: ${note.value.clientWidth}px; margin: -0.75rem -1rem 0; max-width: 122%;` : 'width: 100%; margin: -0.75rem -1rem 0;'
  })

  const condensedTags = computed(() => {
    const note = props.previewNote
    if (note && note.tags) return note.tags.length > 2 ? note.tags.splice(0, 2) : note.tags
    else return ['...']
  })

  const previewNoteStyle = computed(() => {
    let number = 20,
      widthNumber = 50
    let scale = common.getters.screensizeType.value === ScreensizeType.desktop ? 0.9 : 1.2
    for (let i = 0; i < props.length; i++) {
      if (scale >= 0.5) {
        // prevent notes from being too small
        number /= 2
        scale -= 0.05
      }
    }

    const width = Math.random() * widthNumber - 20
    const height = Math.random() * number - 5
    return `left:${width}%; top:${height}%; scale:${scale};`
  })

  const previewImage = computed(() => {
    if (props.previewNote.attachment?.url) {
      const url = props.previewNote.attachment?.url
      if (props.previewNote.attachment.type === AttachmentType.image) return url
      else if (props.previewNote.attachment.type === AttachmentType.external) return `${baseUrl}/api/file?url=${encodeURIComponent(url)}`
    }
    return BrokenImage
  })

  function extractTags(text: string) {
    let newText = text
    if (newText.length > 70) newText = newText.substring(0, 70).concat('...') // display a snippet of the notes text
    for (const url of state.actualUrls) newText = newText.replace(url, '')
    return newText
  }

  function getStyles(color: string) {
    if (color.includes('bg-white')) return 'text-slate-dark'
    else return 'text-slate-white'
  }

  function getKeywordStyle(color: string) {
    if (color.includes('bg-white')) return 'border-color: var(--bg-slate-middle)'
  }

  function imageLoadError(event: Event) {
    state.imageErrorEncountered = true
  }

  function imageLoad(event: Event) {
    state.imageErrorEncountered = false
  }
</script>
<style scoped>
  .preview-note {
    position: relative;
    max-width: 250px;
    min-width: 200px;
    transition: ease-in-out 0.2s;
  }
</style>
